import React from 'react';
import { Link } from 'react-router-dom';
import GlobalOverlayPortal from '../modals/GlobalOverlayPortal';
import '../../../dist/assets/stylesheets/css/notification.css';
import '../../../dist/assets/stylesheets/css/recordPatient.css';

function StatusIcon({ status }) {
  if (status === 'success') {
    return (
      <span
        className="device-connection-check-icon device-connection-check-icon--success"
        data-testid="device-connection-check-icon-success"
        aria-hidden
      >
        <svg viewBox="0 0 24 24" focusable="false">
          <circle cx="12" cy="12" r="10" />
          <path d="M7.5 12.5l3 3 6-6.5" fill="none" />
        </svg>
      </span>
    );
  }
  if (status === 'failed') {
    return (
      <span
        className="device-connection-check-icon device-connection-check-icon--failed"
        data-testid="device-connection-check-icon-failed"
        aria-hidden
      >
        <svg viewBox="0 0 24 24" focusable="false">
          <circle cx="12" cy="12" r="10" />
          <path d="M8 8l8 8M16 8l-8 8" fill="none" />
        </svg>
      </span>
    );
  }
  return (
    <span
      className="device-connection-check-icon device-connection-check-icon--spinner"
      data-testid="device-connection-check-spinner"
      aria-hidden
    />
  );
}

function ConnectionRow({ row }) {
  if (!row) return null;
  const status = row.status || 'pending';
  const showSpinner = status === 'checking' || status === 'pending';
  return (
    <div
      className={`device-connection-check-row device-connection-check-row--${status}`}
      data-testid={`device-connection-check-row-${row.kind}`}
    >
      <StatusIcon status={showSpinner ? 'checking' : status} />
      <span className="device-connection-check-row-label">
        {row.title} {row.current}/{row.total}
      </span>
    </div>
  );
}

/**
 * Full-screen connection check overlay for the Record page.
 * Per-group rows show GoPro k/N and EvoWalk™ k/N; the spinner becomes a
 * circular check or cross when that group finishes.
 */
function DeviceConnectionCheckModal({
  open,
  phase = 'running', // 'running' | 'success' | 'mismatch' | 'error'
  rows = [],
  comparison = null,
  evowalkEnabled = false,
  onRetry,
  /* Set for the post-upload pause: the same card, with only this sentence. */
  message = '',
}) {
  if (!open) return null;

  const camerasMismatch = comparison && comparison.camerasMismatch;
  const evowalksMismatch = comparison && comparison.evowalksMismatch;
  const visibleRows = Array.isArray(rows) ? rows : [];
  const notice = typeof message === 'string' ? message : '';

  return (
    <GlobalOverlayPortal>
      <div
        className="app-global-overlay--fullscreen-backdrop device-connection-check-backdrop"
        data-testid="device-connection-check-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="device-connection-check-title"
      >
        <div className="device-connection-check-card">
          <h2
            id="device-connection-check-title"
            className={`device-connection-check-title${notice ? ' device-connection-check-title--only' : ''}`}
          >
            {notice ? notice : (
              <>
                {phase === 'success' && 'Devices ready'}
                {phase === 'running' && 'Checking connections'}
                {(phase === 'mismatch' || phase === 'error') && 'Connection issue'}
              </>
            )}
          </h2>

          {!notice && (
          <div
            className="device-connection-check-rows"
            data-testid="device-connection-check-rows"
          >
            {visibleRows.map((row) => (
              <ConnectionRow key={row.kind} row={row} />
            ))}
          </div>
          )}

          {!notice && phase === 'success' && (
            <p
              className="device-connection-check-ready-copy"
              data-testid="device-connection-check-success"
            >
              {evowalkEnabled ? 'Cameras and EvoWalk™ are ready' : 'Cameras are ready'}
            </p>
          )}

          {!notice && phase === 'mismatch' && comparison && (
            <div className="device-connection-check-mismatch" data-testid="device-connection-check-mismatch">
              {camerasMismatch && (
                <p>
                  Cameras: expected {comparison.expectedCameraCount} connected,
                  found {comparison.cameraCount}.
                </p>
              )}
              {evowalksMismatch && (
                <p>
                  EvoWalk™: expected {comparison.expectedEvowalkCount} connected,
                  found {comparison.evowalkCount}.
                </p>
              )}
              <div className="device-connection-check-actions">
                {camerasMismatch && (
                  <Link
                    to="/devices"
                    className="device-connection-check-btn"
                    data-testid="device-connection-fix-cameras"
                  >
                    Fix cameras
                  </Link>
                )}
                {evowalksMismatch && (
                  <Link
                    to="/evowalk"
                    className="device-connection-check-btn"
                    data-testid="device-connection-fix-evowalk"
                  >
                    Fix EvoWalk™
                  </Link>
                )}
                {typeof onRetry === 'function' && (
                  <button
                    type="button"
                    className="device-connection-check-btn device-connection-check-btn--secondary"
                    onClick={onRetry}
                    data-testid="device-connection-check-retry"
                  >
                    Check again
                  </button>
                )}
              </div>
            </div>
          )}

          {!notice && phase === 'error' && (
            <div className="device-connection-check-mismatch" data-testid="device-connection-check-error">
              <p>
                {evowalkEnabled
                  ? 'Could not verify cameras or EvoWalk™. Check connections and try again.'
                  : 'Could not verify cameras. Check connections and try again.'}
              </p>
              <div className="device-connection-check-actions">
                <Link
                  to="/devices"
                  className="device-connection-check-btn"
                  data-testid="device-connection-fix-cameras"
                >
                  Open cameras
                </Link>
                {evowalkEnabled && (
                  <Link
                    to="/evowalk"
                    className="device-connection-check-btn"
                    data-testid="device-connection-fix-evowalk"
                  >
                    Open EvoWalk™
                  </Link>
                )}
                {typeof onRetry === 'function' && (
                  <button
                    type="button"
                    className="device-connection-check-btn device-connection-check-btn--secondary"
                    onClick={onRetry}
                    data-testid="device-connection-check-retry"
                  >
                    Check again
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </GlobalOverlayPortal>
  );
}

export default DeviceConnectionCheckModal;
